/**
 * Error thrown when an operation times out.
 * Moved from sdk/utils/TimeoutHelper.ts for centralized error registry.
 */
export class TimeoutError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TimeoutError'
  }
}

/**
 * General media device error thrown by services (MediaStreamService, ScreenShareService, etc.)
 */
export class MediaDeviceError extends Error {
  constructor(
    message: string,
    public readonly originalError: Error,
    public readonly deviceKind?: string
  ) {
    super(message);
    this.name = 'MediaDeviceError';
  }
}

/**
 * Conference audio recording error (MediaRecorder unsupported, no recordable audio, etc.)
 */
export class RecordingError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'RecordingError'
  }
}

/**
 * Base class for all getUserMedia errors.
 * Subclasses preserve the original DOMException and device kind.
 */
export class MediaAccessError extends Error {
  constructor(
    message: string,
    public readonly originalError: Error,
    public readonly deviceKind?: 'video' | 'audio' | 'screen'
  ) {
    super(message)
    this.name = 'MediaAccessError'
  }
}

/** User denied access to camera/microphone (NotAllowedError, PermissionDeniedError) */
export class PermissionDeniedError extends MediaAccessError {
  constructor(originalError: Error, deviceKind?: 'video' | 'audio') {
    super('Permission denied for media device', originalError, deviceKind)
    this.name = 'PermissionDeniedError'
  }
}

export type DeviceUnavailableReason = 'notFound' | 'busy' | 'aborted'

/** Device not found or busy (NotFoundError, NotReadableError, AbortError) */
export class DeviceUnavailableError extends MediaAccessError {
  public readonly reason: DeviceUnavailableReason
  constructor(originalError: Error, deviceKind?: 'video' | 'audio') {
    super('Media device unavailable', originalError, deviceKind)
    this.name = 'DeviceUnavailableError'
    this.reason = originalError.name === 'NotReadableError' ? 'busy'
               : originalError.name === 'AbortError' ? 'aborted'
               : 'notFound'
  }
}

/** Requested device no longer exists -- stale deviceId (OverconstrainedError) */
export class DeviceConstraintError extends MediaAccessError {
  constructor(originalError: Error, deviceKind?: 'video' | 'audio') {
    super('Device constraint cannot be satisfied', originalError, deviceKind)
    this.name = 'DeviceConstraintError'
  }
}

/**
 * Classify a native getUserMedia error into a typed class.
 * Returns the original error when it does not match any known pattern.
 */
export function classifyMediaError(
  error: unknown,
  deviceKind?: 'video' | 'audio'
): Error {
  if (!(error instanceof Error)) return new Error(String(error))

  switch (error.name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return new PermissionDeniedError(error, deviceKind)

    case 'NotFoundError':
    case 'NotReadableError':
    case 'AbortError':
      return new DeviceUnavailableError(error, deviceKind)

    case 'OverconstrainedError':
      return new DeviceConstraintError(error, deviceKind)

    default:
      return error
  }
}
